/**
 * Where was this photo taken?
 *
 * The client asked for a Google Earth pin on the spot the PHOTO was taken, not
 * on the property (feedback fe9ec2ad), so a coordinate has to be captured at the
 * moment of capture. It cannot be recovered later: `downscaleImageForUpload`
 * re-encodes through a canvas, which strips EXIF including GPS, and iOS Safari
 * usually does not write a GPS tag for an in-browser capture in the first place.
 *
 * Three tiers, best first:
 *
 *   1. exif      — GPS parsed from the ORIGINAL file, before downscaling. Rare
 *                  for a live in-browser capture, but it is the only tier that
 *                  works for a gallery import of a photo taken hours earlier,
 *                  where tiers 2 and 3 would record where the tech is standing
 *                  NOW rather than where the photo was taken.
 *   2. live      — navigator.geolocation. The workhorse during an inspection.
 *   3. heartbeat — the tracker's last known fix, when a live read fails or is
 *                  too slow. Within ~30s of truth while tracking is running.
 *
 * PERMISSION RULE: this never prompts. Tier 2 only runs when location is already
 * granted for the session, because a denied OS prompt poisons the permission for
 * the whole origin and takes the Set-GPS button down with it. On a device where
 * permission was never granted (see bug b9b4ab63) all three tiers can miss and
 * the photo simply gets no pin — by design, silently, rather than nagging a tech
 * mid-inspection.
 */
import { getLastKnownFix, isLocationGranted } from './locationState';

export type PhotoGeoSource = 'exif' | 'live' | 'heartbeat';

export interface PhotoGeo {
  lat: number;
  lng: number;
  /** Radius of uncertainty in metres, when the source reports one. */
  acc: number | null;
  src: PhotoGeoSource;
  /** ISO timestamp of the fix. */
  at: string;
}

/** Map of photo URL (or offline marker) -> where it was taken. */
export type PhotoGeoMap = Record<string, PhotoGeo>;

/**
 * Why a tier did or did not produce a location.
 *
 * Every tier deliberately fails closed to null, which makes "no location was
 * available" and "we never got as far as asking" identical from the outside.
 * That is why no photo in production has ever carried a coordinate and nobody
 * could say which of the three tiers was at fault. These outcomes are the
 * difference, and they are what the capture breadcrumb reports.
 */
export type PhotoGeoTierOutcome =
  | 'hit'
  /** Not a JPEG, so there is no EXIF block worth scanning. */
  | 'skipped_not_jpeg'
  /** No navigator.geolocation at all (non-browser or blocked context). */
  | 'skipped_no_geolocation'
  /** Permission not granted this session — reading would raise an OS prompt. */
  | 'skipped_not_granted'
  /** The tracker has never recorded a fix this session. */
  | 'miss_no_fix'
  /** A fix exists but is older than MAX_HEARTBEAT_AGE_MS. */
  | 'miss_stale'
  /** getCurrentPosition neither resolved nor rejected inside the timeout. */
  | 'miss_timeout'
  /** Ran, found nothing usable (no GPS IFD, position error, malformed file). */
  | 'miss';

/**
 * What happened on one capture. Small and flat on purpose: it goes straight
 * into an event_logs metadata blob and has to stay readable in the admin Event
 * Log viewer without tooling.
 */
export interface PhotoGeoDiagnostic {
  /** Winning tier, or 'none' when the photo gets no pin. */
  src: PhotoGeoSource | 'none';
  exif: PhotoGeoTierOutcome;
  live: PhotoGeoTierOutcome;
  heartbeat: PhotoGeoTierOutcome;
  /** Session grant as the tiers saw it — the gate on the live read. */
  granted: boolean;
  /** Age of the tracker's last fix, or null if it has never had one. */
  fix_age_ms: number | null;
  file_type: string;
  file_bytes: number;
  elapsed_ms: number;
}

interface TierResult {
  geo: PhotoGeo | null;
  outcome: PhotoGeoTierOutcome;
}

/** A live read must not hold up the UI; the heartbeat fallback is close enough. */
const LIVE_TIMEOUT_MS = 4000;
/** Older than this and the tracker has probably stopped — better no pin than a
 *  pin at the last property the tech visited. */
const MAX_HEARTBEAT_AGE_MS = 5 * 60_000;
/** Only read the leading bytes; GPS lives in the first APP1 segment. */
const EXIF_SCAN_BYTES = 256 * 1024;

// ---------------------------------------------------------------------------
// Tier 1 — EXIF
// ---------------------------------------------------------------------------

/**
 * Minimal JPEG EXIF GPS reader. Deliberately not a dependency: we need exactly
 * two tags out of one IFD, and a general EXIF library is a large amount of code
 * shipped to a field iPad on a weak connection to do it.
 *
 * Walks JPEG markers to the APP1/Exif segment, reads the TIFF header for byte
 * order, finds the GPS IFD pointer (tag 0x8825) in IFD0, then reads latitude
 * (0x0002) + its N/S ref (0x0001) and longitude (0x0004) + its E/W ref (0x0003).
 * Returns null on anything unexpected — a missing tag, a HEIC (GPS lives in a
 * different container a JPEG parser cannot read), or a truncated file.
 */
export function parseExifGps(buf: ArrayBuffer): { lat: number; lng: number } | null {
  try {
    const view = new DataView(buf);
    if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return null; // not a JPEG

    // Walk segments looking for APP1 (0xFFE1) carrying an "Exif\0\0" header.
    let offset = 2;
    let tiff = -1;
    while (offset + 4 <= view.byteLength) {
      if (view.getUint8(offset) !== 0xff) break; // desynced — give up
      const marker = view.getUint8(offset + 1);
      if (marker === 0xda) break; // start of scan: no EXIF before the image data
      const size = view.getUint16(offset + 2);
      if (size < 2) break;
      if (marker === 0xe1 && offset + 10 <= view.byteLength && view.getUint32(offset + 4) === 0x45786966) {
        tiff = offset + 10; // skip "Exif\0\0"
        break;
      }
      offset += 2 + size;
    }
    if (tiff < 0 || tiff + 8 > view.byteLength) return null;

    // TIFF header: byte order, magic 42, offset of IFD0.
    const le = view.getUint16(tiff) === 0x4949;
    if (view.getUint16(tiff + 2, le) !== 42) return null;
    const ifd0 = tiff + view.getUint32(tiff + 4, le);
    if (ifd0 + 2 > view.byteLength) return null;

    // Find the GPS IFD pointer in IFD0.
    const entries = view.getUint16(ifd0, le);
    let gpsIfd = -1;
    for (let i = 0; i < entries; i++) {
      const entry = ifd0 + 2 + i * 12;
      if (entry + 12 > view.byteLength) return null;
      if (view.getUint16(entry, le) === 0x8825) {
        gpsIfd = tiff + view.getUint32(entry + 8, le);
        break;
      }
    }
    if (gpsIfd < 0 || gpsIfd + 2 > view.byteLength) return null;

    // Read the four GPS tags we care about.
    const gpsEntries = view.getUint16(gpsIfd, le);
    let latRef = '';
    let lngRef = '';
    let lat: number | null = null;
    let lng: number | null = null;

    /** Three RATIONALs (deg, min, sec) -> signed decimal degrees. */
    const readDms = (valueOffset: number): number | null => {
      if (valueOffset + 24 > view.byteLength) return null;
      let deg = 0;
      for (let i = 0; i < 3; i++) {
        const num = view.getUint32(valueOffset + i * 8, le);
        const den = view.getUint32(valueOffset + i * 8 + 4, le);
        if (den === 0) return null;
        deg += num / den / 60 ** i;
      }
      return deg;
    };

    for (let i = 0; i < gpsEntries; i++) {
      const entry = gpsIfd + 2 + i * 12;
      if (entry + 12 > view.byteLength) return null;
      const tag = view.getUint16(entry, le);
      const count = view.getUint32(entry + 4, le);
      // A value larger than 4 bytes is stored elsewhere and the entry holds a
      // pointer; refs are a single ASCII char and sit inline.
      if (tag === 0x0001 || tag === 0x0003) {
        const ch = String.fromCharCode(view.getUint8(entry + 8));
        if (tag === 0x0001) latRef = ch;
        else lngRef = ch;
      } else if ((tag === 0x0002 || tag === 0x0004) && count === 3) {
        const value = readDms(tiff + view.getUint32(entry + 8, le));
        if (value === null) return null;
        if (tag === 0x0002) lat = value;
        else lng = value;
      }
    }

    if (lat === null || lng === null) return null;
    if (latRef === 'S') lat = -lat;
    if (lngRef === 'W') lng = -lng;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
    // 0,0 is Null Island — in practice a cleared or bogus tag, not a location.
    if (lat === 0 && lng === 0) return null;
    return { lat, lng };
  } catch {
    return null; // best-effort: a malformed file must never block a photo
  }
}

async function fromExif(file: File): Promise<TierResult> {
  try {
    if (!/jpe?g$/i.test(file.type) && !/\.jpe?g$/i.test(file.name)) {
      return { geo: null, outcome: 'skipped_not_jpeg' };
    }
    const slice = file.slice(0, EXIF_SCAN_BYTES);
    const gps = parseExifGps(await slice.arrayBuffer());
    if (!gps) return { geo: null, outcome: 'miss' };
    // EXIF carries no accuracy figure, and the timestamp is the photo's own.
    return {
      geo: { lat: gps.lat, lng: gps.lng, acc: null, src: 'exif', at: new Date(file.lastModified).toISOString() },
      outcome: 'hit',
    };
  } catch {
    return { geo: null, outcome: 'miss' };
  }
}

// ---------------------------------------------------------------------------
// Tier 2 — live position (only when already granted)
// ---------------------------------------------------------------------------

function fromLive(): Promise<TierResult> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return Promise.resolve({ geo: null, outcome: 'skipped_no_geolocation' });
  }
  // never prompt: a denied OS dialog poisons the permission for the whole origin
  if (!isLocationGranted()) return Promise.resolve({ geo: null, outcome: 'skipped_not_granted' });
  return new Promise((resolve) => {
    let settled = false;
    const done = (v: TierResult) => {
      if (!settled) {
        settled = true;
        resolve(v);
      }
    };
    // Belt and braces: some iOS builds neither resolve nor reject a
    // getCurrentPosition call, which would leave the photo waiting forever.
    const timer = setTimeout(() => done({ geo: null, outcome: 'miss_timeout' }), LIVE_TIMEOUT_MS);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(timer);
        done({
          geo: {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            acc: pos.coords.accuracy != null ? Math.round(pos.coords.accuracy) : null,
            src: 'live',
            at: new Date().toISOString(),
          },
          outcome: 'hit',
        });
      },
      () => {
        clearTimeout(timer);
        done({ geo: null, outcome: 'miss' });
      },
      { enableHighAccuracy: true, timeout: LIVE_TIMEOUT_MS, maximumAge: 30_000 },
    );
  });
}

// ---------------------------------------------------------------------------
// Tier 3 — the tracker's last fix
// ---------------------------------------------------------------------------

function fromHeartbeat(): TierResult {
  const fix = getLastKnownFix();
  if (!fix) return { geo: null, outcome: 'miss_no_fix' };
  if (Date.now() - fix.at > MAX_HEARTBEAT_AGE_MS) return { geo: null, outcome: 'miss_stale' };
  return {
    geo: {
      lat: fix.lat,
      lng: fix.lng,
      acc: fix.accuracyM,
      src: 'heartbeat',
      at: new Date(fix.at).toISOString(),
    },
    outcome: 'hit',
  };
}

// ---------------------------------------------------------------------------

/**
 * Best available location for a photo about to be uploaded. Never throws and
 * never prompts; returns null when nothing is available, which simply means the
 * photo gets no pin.
 *
 * Call this with the ORIGINAL file, before downscaling.
 */
export async function resolvePhotoLocation(file: File): Promise<PhotoGeo | null> {
  return (await resolvePhotoLocationDetailed(file)).geo;
}

/**
 * Same resolution, plus why each tier did or did not fire.
 *
 * There is deliberately only ONE resolution path: `resolvePhotoLocation` calls
 * this and drops the diagnostic. A separate "diagnostic mode" that re-ran the
 * tiers could disagree with the real one, which would make it worse than
 * useless — the whole point is to explain captures that actually happened.
 *
 * Tiers short-circuit, so a tier after the winner never runs. Its outcome is
 * reported as 'skipped_*' where the reason is knowable without side effects,
 * and otherwise left at the value it would have had. Never throws.
 */
export async function resolvePhotoLocationDetailed(
  file: File,
): Promise<{ geo: PhotoGeo | null; diag: PhotoGeoDiagnostic }> {
  const startedAt = Date.now();
  const fix = getLastKnownFix();
  const diag: PhotoGeoDiagnostic = {
    src: 'none',
    exif: 'miss',
    live: 'miss',
    heartbeat: 'miss_no_fix',
    granted: isLocationGranted(),
    fix_age_ms: fix ? Date.now() - fix.at : null,
    file_type: file.type || '',
    file_bytes: file.size,
    elapsed_ms: 0,
  };
  const finish = (geo: PhotoGeo | null) => {
    diag.src = geo ? geo.src : 'none';
    diag.elapsed_ms = Date.now() - startedAt;
    return { geo, diag };
  };

  try {
    const exif = await fromExif(file);
    diag.exif = exif.outcome;
    if (exif.geo) return finish(exif.geo);

    const live = await fromLive();
    diag.live = live.outcome;
    if (live.geo) return finish(live.geo);

    const heartbeat = fromHeartbeat();
    diag.heartbeat = heartbeat.outcome;
    return finish(heartbeat.geo);
  } catch {
    // Unreachable in practice — every tier already fails closed — but a photo
    // must never be blocked by its own geotagging.
    return finish(null);
  }
}

/** Move an entry to a new key, for when an offline marker becomes a real URL. */
export function rekeyPhotoGeo(map: PhotoGeoMap, from: string, to: string): PhotoGeoMap {
  const entry = map[from];
  if (!entry) return map;
  const next = { ...map, [to]: entry };
  delete next[from];
  return next;
}

/** Drop entries whose photo is no longer attached, so the map cannot grow
 *  unboundedly as photos are added and removed during an inspection. */
export function prunePhotoGeo(map: PhotoGeoMap, keepUrls: string[]): PhotoGeoMap {
  const keep = new Set(keepUrls);
  const next: PhotoGeoMap = {};
  for (const [url, geo] of Object.entries(map)) {
    if (keep.has(url)) next[url] = geo;
  }
  return next;
}
